// FotoHallenEinwilligung.jsx — darf das Wettkämpferfoto auf Hallenbildschirmen
// und in der Live-Ansicht gezeigt werden? (21.10.2026)
//
// Zwei Fälle in einer Komponente (pages/Club/VereinDetailPage.jsx):
//   - datei gesetzt: ein Foto wurde gewählt; erst nach der Antwort geht es an
//     POST /api/wettkaempfer/:id/foto (mit foto_anzeige_einwilligung).
//   - sonst, wenn ein Foto da ist: Stand anzeigen und nachträglich ändern
//     (PUT /api/wettkaempfer/:id/foto-anzeige).
// Der Text kommt vom Server (GET /api/wettkaempfer/foto-anzeige/text), damit
// er zur gespeicherten Fassung passt (backend/utils/fotoFreigabe.js).
import React, { useEffect, useState } from 'react';
import './FotoHallenEinwilligung.css';

const kopf = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });

async function antwort(r) {
  const d = await r.json().catch(() => ({}));
  if (!r.ok || d.success === false) throw new Error(d.error || d.message || `Fehler ${r.status}`);
  return d;
}

/** Heute unter 18? Ohne Geburtsdatum vorsichtshalber ja (wie der Server). */
function minderjaehrig(geburtsdatum) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(geburtsdatum || ''));
  if (!m) return true;
  const h = new Date();
  let alter = h.getFullYear() - Number(m[1]);
  const mon = h.getMonth() + 1, tag = h.getDate();
  if (mon < Number(m[2]) || (mon === Number(m[2]) && tag < Number(m[3]))) alter -= 1;
  return alter < 18;
}

const STAND = { 1: 'Ja, wird gezeigt', 0: 'Nein, wird nicht gezeigt' };

export default function FotoHallenEinwilligung({ wk, datei, onAbbrechen, onGespeichert, onFehler }) {
  const [texte, setTexte] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    let ab = false;
    fetch('/api/wettkaempfer/foto-anzeige/text', { headers: kopf() })
      .then(antwort)
      .then((d) => { if (!ab) setTexte(d.texte); })
      .catch((e) => { if (!ab) setFehler(`Einwilligungstext nicht ladbar: ${e.message}`); });
    return () => { ab = true; };
  }, []);

  if (!wk) return null;
  const kind = minderjaehrig(wk.geburtsdatum);
  const text = texte ? (kind ? texte.erziehungsberechtigte : texte.selbst) : '';

  const melden = (e) => { setFehler(e.message); onFehler?.(e.message); };

  const hochladen = async (ja) => {
    setLaeuft(true); setFehler('');
    try {
      const fd = new FormData();
      fd.append('foto_anzeige_einwilligung', ja ? 'ja' : 'nein');
      fd.append('foto', datei);
      const d = await antwort(await fetch(`/api/wettkaempfer/${wk.wettkaempfer_id}/foto`, { method: 'POST', headers: kopf(), body: fd }));
      onGespeichert?.({ foto: d.foto, foto_anzeige_einwilligung: d.foto_anzeige.einwilligung, foto_anzeige_art: d.foto_anzeige.art });
    } catch (e) { melden(e); } finally { setLaeuft(false); }
  };

  const aendern = async (ja) => {
    setLaeuft(true); setFehler('');
    try {
      const d = await antwort(await fetch(`/api/wettkaempfer/${wk.wettkaempfer_id}/foto-anzeige`, {
        method: 'PUT', headers: { ...kopf(), 'Content-Type': 'application/json' }, body: JSON.stringify({ einwilligung: ja }),
      }));
      onGespeichert?.({ foto_anzeige_einwilligung: d.foto_anzeige.einwilligung, foto_anzeige_art: d.foto_anzeige.art });
    } catch (e) { melden(e); } finally { setLaeuft(false); }
  };

  if (datei) {
    return (
      <div className="fhe-box fhe-frage" role="dialog" aria-label="Foto auf Hallenbildschirmen">
        <b>Darf dieses Foto auf den Bildschirmen in der Halle und in der Live-Ansicht gezeigt werden?</b>
        {kind && <span className="fhe-hinweis">Minderjährig: Der Verein bestätigt die Einwilligung der Erziehungsberechtigten.</span>}
        <p>{text || 'Text wird geladen …'}</p>
        <div className="fhe-knoepfe">
          <button type="button" className="fhe-ja" disabled={laeuft || !texte} onClick={() => hochladen(true)}>
            {kind ? 'Einwilligung liegt vor, zeigen' : 'Ja, zeigen'}
          </button>
          <button type="button" disabled={laeuft || !texte} onClick={() => hochladen(false)}>Nein, nicht zeigen</button>
          <button type="button" className="fhe-leise" disabled={laeuft} onClick={onAbbrechen}>Abbrechen</button>
        </div>
        <span className="fhe-leise-text">Ohne Einwilligung zeigen die Bildschirme das Vereinslogo.</span>
        {fehler && <span className="fhe-fehler" role="alert">{fehler}</span>}
      </div>
    );
  }

  if (!wk.foto) return null;
  const e = wk.foto_anzeige_einwilligung;
  const offen = e === null || e === undefined;
  const stand = offen ? 'Nicht gefragt, wird nicht gezeigt' : STAND[Number(e)] || 'Nicht gefragt, wird nicht gezeigt';
  return (
    <div className="fhe-box">
      <span className="fhe-titel">Foto auf Hallenbildschirmen und in der Live-Ansicht</span>
      <span className={`fhe-stand${Number(e) === 1 ? ' fhe-stand--ja' : ''}`} data-foto-anzeige={e ?? 'offen'}>{stand}</span>
      <div className="fhe-knoepfe">
        {Number(e) !== 1 && (
          <button type="button" className="fhe-ja" disabled={laeuft || !texte} onClick={() => aendern(true)} title={text}>
            {kind ? 'Einwilligung liegt vor' : 'Zeigen erlauben'}
          </button>
        )}
        {(Number(e) === 1 || offen) && (
          <button type="button" disabled={laeuft} onClick={() => aendern(false)}>{offen ? 'Nicht zeigen' : 'Nicht mehr zeigen'}</button>
        )}
      </div>
      {Number(e) !== 1 && text && <p className="fhe-leise-text">{text}</p>}
      {fehler && <span className="fhe-fehler" role="alert">{fehler}</span>}
    </div>
  );
}
